import { FormController, FormFileUpload } from '@/components/form';
import {
  ATTACHMENT_MAX_FILE_SIZE_BYTES,
  ATTACHMENT_MAX_FILE_SIZE_LABEL,
} from '@/constants/uploadLimits';
import { CoreApi, SubmittalsApi } from '@/state/api';
import { CoreType } from '@/types';
import {
  Badge,
  Button,
  Center,
  FileUploadFileMimeType,
  Flex,
  Input,
  Separator,
  Spinner,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { OverlayDialog, toasterApi } from '@ui';
import dayjs from 'dayjs';
import React, { Suspense, useMemo, useRef, useState } from 'react';
import { Control, FieldErrors, UseFormRegister } from 'react-hook-form';
import { LuDot } from 'react-icons/lu';
import z from 'zod';
import ManageCard from '../ManageCard';

import { toDateInputValue } from '@/utils/dateFields.ts';
import { getFileNameWithoutExtension } from '@/utils/utilFunctions.ts';
import ViewReturnAttachmentsCard from '../../RfiLogs/ViewReturnAttachmentsCard/index.tsx';
const ViewAttachmentsCard = React.lazy(() => import('../ViewAttachmentsCard'));

type SubmittalDetailsProps = {
  submittal_id: number | null;
  onClose: () => void;
};

const SubmittalDetails = ({ submittal_id, onClose }: SubmittalDetailsProps) => {
  const [postAttachmentsPresignedUrlsMutation] =
    CoreApi.Api.usePostAttachmentsPresignedUrlsMutation();

  const [postSubmittalAttachmentsMutation] =
    SubmittalsApi.Api.usePostSubmittalAttachmentsMutation();

  const [deleteSubmittalMutation] =
    SubmittalsApi.Api.useDeleteSubmittalMutation();

  const [postSubmittalExportPdfMutation] =
    SubmittalsApi.Api.usePostSubmittalExportPdfMutation();

  const [postSubmittalReturnAttachmentMutation] =
    SubmittalsApi.Api.usePostSubmittalReturnAttachmentMutation();

  const [deleteSubmittalReturnAttachmentMutation] =
    SubmittalsApi.Api.useDeleteSubmittalReturnAttachmentMutation();

  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);

  const canFetch = typeof submittal_id === 'number' && submittal_id > 0;

  const { data } = SubmittalsApi.Api.useGetSubmittalQuery(
    {
      params: {
        submittal_id: submittal_id as number,
      },
    },
    { skip: !canFetch },
  );

  const { data: returnAttachmentsData } =
    SubmittalsApi.Api.useGetSubmittalReturnAttachmentsQuery(
      {
        params: {
          submittal_id: submittal_id as number,
        },
      },
      { skip: !canFetch },
    );

  const returnAttachments = returnAttachmentsData ?? [];
  const submittal = data?.data;

  const formattedDue = useMemo(() => {
    const date = toDateInputValue(submittal?.due_at);
    if (!date) return null;

    return {
      date,
      days: dayjs().diff(dayjs(submittal?.due_at), 'day'),
    };
  }, [submittal?.due_at]);

  const formattedSubmitted = toDateInputValue(submittal?.submitted_at);
  const formattedReturned = toDateInputValue(submittal?.returned_at);

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  type SubmittalExportPdfModalContentProps = {
    submittal_id: number;
    submittal: any;
    postSubmittalExportPdfMutation: any;
    downloadBlob: (blob: Blob, filename: string) => void;
  };

  const SubmittalExportPdfModalContent = ({
    submittal_id,
    submittal,
    postSubmittalExportPdfMutation,
    downloadBlob,
  }: SubmittalExportPdfModalContentProps) => {
    const [isExportingPdf, setIsExportingPdf] = useState(false);
    const exportInFlightRef = useRef(false);

    return (
      <Flex position="relative" flexDirection="column" gap={4} width="full">
        {isExportingPdf && (
          <Flex
            position="absolute"
            inset={0}
            zIndex={10}
            bg="rgba(0,0,0,0.35)"
            backdropFilter="blur(2px)"
            alignItems="center"
            justifyContent="center"
            borderRadius="md"
          >
            <Flex
              bg="bg"
              border="1px solid"
              borderColor="border"
              borderRadius="lg"
              p={4}
              alignItems="center"
              gap={3}
              boxShadow="lg"
            >
              <Spinner />
              <Text fontWeight="semibold">Preparing PDF…</Text>
            </Flex>
          </Flex>
        )}

        <FormController
          size="lg"
          formSchema={submittalExportPdfFormSchema}
          defaultValues={{
            to_name: submittal?.export_to_name || '',
            to_address: submittal?.export_to_address || '',
            attention: submittal?.export_attention || '',
            items_shipped_via: submittal?.export_items_shipped_via || '',
            items_shipped_other: submittal?.export_items_shipped_other || '',
            these_items_submitted:
              submittal?.export_these_items_submitted || 'out_for_approval',
            remarks: '',
            copy_to: '',
          }}
          disableButton={isExportingPdf}
          ButtonLabel="Submit"
          onFormSubmit={async (formData) => {
            if (exportInFlightRef.current) return;

            exportInFlightRef.current = true;
            setIsExportingPdf(true);
            try {
              const copy_to =
                formData?.copy_to
                  ?.split(',')
                  .map((s) => s.trim())
                  .filter(Boolean) ?? [];

              const blob = await postSubmittalExportPdfMutation({
                params: { submittal_id },
                body: {
                  to_name: formData?.to_name || null,
                  to_address: formData?.to_address || null,
                  attention: formData?.attention || null,
                  items_shipped_via:
                    (formData?.items_shipped_via as any) ?? null,
                  items_shipped_other: formData?.items_shipped_other || null,
                  these_items_submitted:
                    (formData?.these_items_submitted as any) ?? null,
                  remarks: formData?.remarks || null,
                  copy_to,
                },
              }).unwrap();

              const safeNumber = (submittal?.number || 'SUBMITTAL').replaceAll(
                '/',
                '-',
              );
              const rev = submittal?.revision ?? 0;
              downloadBlob(blob, `${safeNumber}-R${rev}.pdf`);

              toasterApi({
                type: 'success',
                status: 200,
                code: 'Exported',
                message: 'Complete PDF prepared successfully',
              });

              OverlayDialog.close('submittalExportPdf');
            } catch (e: any) {
              toasterApi({
                type: 'error',
                status: e?.status ?? 500,
                code: 'Error',
                message:
                  e?.data?.detail || e?.message || 'Failed to export PDF',
              });
            } finally {
              exportInFlightRef.current = false;
              setIsExportingPdf(false);
            }
          }}
        >
          {({
            register,
          }: {
            register: UseFormRegister<SubmittalExportPdfFormType>;
            control: Control<SubmittalExportPdfFormType>;
            errors: FieldErrors<SubmittalExportPdfFormType>;
          }) => {
            return (
              <Flex
                flexDirection="column"
                gap={4}
                pointerEvents={isExportingPdf ? 'none' : 'auto'}
              >
                <Flex flexDirection="column" gap={2}>
                  <Text fontWeight="bold">To</Text>
                  <Input placeholder="To name" {...register('to_name')} />
                  <Textarea
                    placeholder="To address (multi-line)"
                    {...register('to_address')}
                  />
                  <Input
                    placeholder="Attention (optional)"
                    {...register('attention')}
                  />
                </Flex>

                <Separator />

                <Flex flexDirection="column" gap={2}>
                  <Text fontWeight="bold">Items Shipped Via</Text>
                  <select
                    {...register('items_shipped_via')}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: 'rgba(255,255,255,0.2)',
                      background: 'transparent',
                      color: 'inherit',
                    }}
                  >
                    <option value="">Select method</option>
                    <option value="us_mail">US Mail</option>
                    <option value="overnight">Overnight</option>
                    <option value="courier">Courier</option>
                    <option value="karas_delivered">Karas Delivered</option>
                    <option value="ground">Ground</option>
                    <option value="uploaded_procore">
                      Uploaded to Procore
                    </option>
                    <option value="email">Email</option>
                    <option value="other">Other (as noted)</option>
                  </select>

                  <Input
                    placeholder="If Other, specify..."
                    {...register('items_shipped_other')}
                  />
                </Flex>

                <Separator />

                <Flex flexDirection="column" gap={2}>
                  <Text fontWeight="bold">These Items Are Submitted As</Text>

                  <select
                    {...register('these_items_submitted')}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: 'rgba(255,255,255,0.2)',
                      background: 'transparent',
                      color: 'inherit',
                    }}
                  >
                    <option value="">Select submission intent</option>
                    <option value="for_reference_only">
                      For reference only
                    </option>
                    <option value="for_information_only">
                      For information only
                    </option>
                    <option value="for_review_comment">
                      For review &amp; comment
                    </option>
                    <option value="out_for_approval">Out for approval</option>
                    <option value="for_final_approval">
                      For final approval
                    </option>
                    <option value="for_record_distribution">
                      For record/distribution
                    </option>
                  </select>
                </Flex>

                <Separator />

                <Flex flexDirection="column" gap={2}>
                  <Text fontWeight="bold">Remarks</Text>
                  <Textarea placeholder="Remarks..." {...register('remarks')} />

                  <Text fontWeight="bold">Copy To</Text>
                  <Input
                    placeholder="Comma-separated list (e.g. Project File, 946/Submittals)"
                    {...register('copy_to')}
                  />
                </Flex>
              </Flex>
            );
          }}
        </FormController>
      </Flex>
    );
  };

  const openPrintPdf = () => {
    if (!submittal_id) return;

    OverlayDialog.open('submittalExportPdf', {
      overlayId: 'submittalExportPdf',
      title: 'Print to PDF',
      content: (
        <SubmittalExportPdfModalContent
          submittal_id={submittal_id}
          submittal={submittal}
          postSubmittalExportPdfMutation={postSubmittalExportPdfMutation}
          downloadBlob={downloadBlob}
        />
      ),
    });
  };

  const openDeleteConfirm = () => {
    if (!submittal_id) return;

    OverlayDialog.open('confirmDeleteSubmittal', {
      overlayId: 'confirmDeleteSubmittal',
      title: 'Delete Submittal?',
      content: (
        <Flex flexDirection="column" gap={4} width="full">
          <Text>
            This will permanently delete this submittal and all its attachments.
            This cannot be undone.
          </Text>

          <Flex justifyContent="flex-end" gap={2}>
            <Button
              variant="outline"
              onClick={() => OverlayDialog.close('confirmDeleteSubmittal')}
            >
              Cancel
            </Button>

            <Button
              colorPalette="red"
              onClick={async () => {
                if (!submittal_id) return;

                try {
                  await deleteSubmittalMutation({
                    params: { submittal_id },
                  }).unwrap();

                  toasterApi({
                    type: 'success',
                    status: 204,
                    code: 'Deleted',
                    message: 'Submittal deleted successfully',
                  });

                  OverlayDialog.close('confirmDeleteSubmittal');
                  onClose();
                } catch (e: any) {
                  toasterApi({
                    type: 'error',
                    status: 500,
                    code: 'Error',
                    message:
                      e?.data?.detail ||
                      e?.message ||
                      'Failed to delete submittal',
                  });
                }
              }}
            >
              Delete
            </Button>
          </Flex>
        </Flex>
      ),
    });
  };

  const handleSubmitReturnAttachments = () => {
    OverlayDialog.open('submittalReturnAttachments', {
      overlayId: 'submittalReturnAttachments',
      title: 'Add Returned Documents',
      content: (
        <FormController
          size="lg"
          formSchema={fileUploadFormSchema}
          defaultValues={{
            attachmentFiles: [],
          }}
          onFormSubmit={async (formData) => {
            if (!formData?.attachmentFiles?.length || !submittal_id) {
              OverlayDialog.close('submittalReturnAttachments');
              return;
            }

            try {
              const getPresignedUrlsPayload: CoreType.PostAttachmentsPresignedUrls['body'] =
                formData?.attachmentFiles?.map((file) => ({
                  module: 'rfi_submittal_return',
                  filename: getFileNameWithoutExtension(file.name),
                  content_type: file.type,
                  size: file.size,
                }));

              const getPresignedUrls =
                await postAttachmentsPresignedUrlsMutation({
                  body: getPresignedUrlsPayload,
                }).unwrap();

              const uploadResults = await Promise.allSettled(
                getPresignedUrls?.map(async (item) => {
                  const file = formData?.attachmentFiles?.find(
                    (f) =>
                      getFileNameWithoutExtension(f.name) === item.filename,
                  );
                  if (!file)
                    throw new Error(`File not found: ${item.filename}`);

                  const res = await fetch(item.upload_url, {
                    method: 'PUT',
                    headers: { 'Content-Type': file.type },
                    body: file,
                  });

                  if (!res.ok)
                    throw new Error(`Upload failed for ${item.filename}`);

                  return {
                    filename: item.filename,
                    file: item.file_url,
                  };
                }),
              );

              const successfullyUploadedFiles = uploadResults
                .filter((r) => r.status === 'fulfilled')
                .map((r) => (r as PromiseFulfilledResult<any>).value);

              for (const file of successfullyUploadedFiles) {
                await postSubmittalReturnAttachmentMutation({
                  body: file,
                  params: { submittal_id },
                }).unwrap();
              }

              toasterApi({
                type: 'success',
                status: 200,
                code: 'Success',
                message: 'Returned documents added successfully',
              });
            } catch {
              toasterApi({
                type: 'error',
                status: 500,
                code: 'Error',
                message: 'Failed to add one or more returned documents',
              });
            } finally {
              OverlayDialog.close('submittalReturnAttachments');
            }
          }}
        >
          {({
            register,
            control,
            errors,
          }: {
            register: UseFormRegister<FileUploadFormType>;
            control: Control<FileUploadFormType>;
            errors: FieldErrors<FileUploadFormType>;
          }) => {
            return (
              <Flex flexDirection="column" gapY={8}>
                <Flex flexDirection="column" gapY={4}>
                  <Text fontSize="md" fontWeight="bold">
                    Upload Returned Documents
                  </Text>
                  <FormFileUpload
                    control={control}
                    register={register}
                    errors={errors}
                    id="attachmentFiles"
                    label=""
                    maxFiles={10}
                    maxFileSize={ATTACHMENT_MAX_FILE_SIZE_BYTES}
                    accept={
                      [
                        'application/pdf',
                        'application/msword',
                        'image/*',
                      ] as FileUploadFileMimeType[]
                    }
                  />
                </Flex>
              </Flex>
            );
          }}
        </FormController>
      ),
    });
  };

  const handleSubmitAttachments = () => {
    OverlayDialog.open('submittalAttachments', {
      overlayId: 'submittalAttachments',
      title: 'Add Submittal Attachments',
      content: (
        <FormController
          size="lg"
          formSchema={fileUploadFormSchema}
          defaultValues={{
            attachmentFiles: [],
          }}
          disableButton={isUploadingAttachments}
          ButtonLabel={
            isUploadingAttachments ? (
              <Flex alignItems="center" gap={2}>
                <Spinner size="sm" />
                <Text>Uploading...</Text>
              </Flex>
            ) : (
              'Submit'
            )
          }
          buttonProps={{
            alignSelf: 'center',
          }}
          onFormSubmit={async (formData) => {
            if (!submittal_id) {
              OverlayDialog.close('submittalAttachments');
              return;
            }

            if (!formData?.attachmentFiles?.length) {
              OverlayDialog.close('submittalAttachments');
              return;
            }

            if (isUploadingAttachments) return;

            setIsUploadingAttachments(true);
            try {
              const getPresignedUrlsPayload: CoreType.PostAttachmentsPresignedUrls['body'] =
                formData?.attachmentFiles?.map((file) => ({
                  module: 'submittal',
                  filename: getFileNameWithoutExtension(file.name),
                  content_type: file.type,
                  size: file.size,
                }));

              const getPresignedUrls =
                await postAttachmentsPresignedUrlsMutation({
                  body: getPresignedUrlsPayload,
                }).unwrap();

              const uploadResults = await Promise.allSettled(
                getPresignedUrls?.map(async (item) => {
                  const file = formData?.attachmentFiles?.find(
                    (f) =>
                      getFileNameWithoutExtension(f.name) === item.filename,
                  );
                  if (!file)
                    throw new Error(`File not found: ${item.filename}`);

                  const res = await fetch(item.upload_url, {
                    method: 'PUT',
                    headers: { 'Content-Type': file.type },
                    body: file,
                  });

                  if (!res.ok)
                    throw new Error(`Upload failed for ${item.filename}`);

                  return {
                    filename: item.filename,
                    file: item.file_url,
                  };
                }),
              );

              const successfullyUploadedFiles = uploadResults
                .filter((r) => r.status === 'fulfilled')
                .map((r: any) => r.value);

              if (successfullyUploadedFiles?.length) {
                await postSubmittalAttachmentsMutation({
                  body: successfullyUploadedFiles,
                  params: { submittal_id },
                }).unwrap();
              }

              toasterApi({
                type: 'success',
                status: 200,
                code: 'Success',
                message: 'Attachments added successfully',
              });
            } catch (e: any) {
              toasterApi({
                type: 'error',
                status: 500,
                code: 'Error',
                message:
                  e?.data?.detail ||
                  e?.message ||
                  'Failed to add one or more attachments',
              });
            } finally {
              setIsUploadingAttachments(false);
              OverlayDialog.close('submittalAttachments');
            }
          }}
        >
          {({
            register,
            control,
            errors,
          }: {
            register: UseFormRegister<FileUploadFormType>;
            control: Control<FileUploadFormType>;
            errors: FieldErrors<FileUploadFormType>;
          }) => {
            return (
              <Flex flexDirection="column" gapY={8}>
                <Flex flexDirection="column" gapY={4}>
                  <Text fontSize="md" fontWeight="bold">
                    Upload Attachment Files
                  </Text>
                  <FormFileUpload
                    control={control}
                    register={register}
                    errors={errors}
                    id="attachmentFiles"
                    label=""
                    maxFiles={10}
                    maxFileSize={ATTACHMENT_MAX_FILE_SIZE_BYTES}
                    accept={
                      [
                        'application/pdf',
                        'application/msword',
                        'image/*',
                      ] as FileUploadFileMimeType[]
                    }
                  />
                </Flex>
              </Flex>
            );
          }}
        </FormController>
      ),
    });
  };

  return (
    <Flex flexDirection="column" gapY={4}>
      <Flex justifyContent="flex-end">
        <Flex flexDirection="column" gapY={2}>
          <Flex gapX={2} justifyContent="flex-end">
            <Button
              size="sm"
              variant="outline"
              color="fg"
              borderColor="fg"
              onClick={onClose}
            >
              Back
            </Button>
            <Button size="sm" onClick={openPrintPdf}>
              Print to PDF
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorPalette="red"
              onClick={openDeleteConfirm}
            >
              Delete
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorPalette="blue"
              onClick={() => {
                OverlayDialog.open('editSubmittal', {
                  overlayId: 'editSubmittal',
                  title: 'Edit Submittal',
                  content: (
                    <ManageCard
                      isNew={false}
                      defaultSubmittalData={submittal?.id ? submittal : null}
                      overlayId="editSubmittal"
                    />
                  ),
                });
              }}
            >
              Edit
            </Button>
          </Flex>
        </Flex>
      </Flex>

      <Flex flexDirection="column" gapY={5}>
        <Flex gapX={2} alignItems="center">
          <Text fontWeight="bold" fontSize="large">
            {submittal?.number}
          </Text>
          <Badge colorPalette="blue">
            <Text textTransform="capitalize">{submittal?.status}</Text>
          </Badge>
        </Flex>

        <Text fontWeight="bold" fontSize="large">
          {submittal?.title}
        </Text>

        <Separator />

        <Flex>
          <Flex flex={1} flexDirection="column" gapY={2}>
            <Flex gapX={2}>
              <Text fontWeight="bold" width="200px">
                Description:
              </Text>
              <Text textTransform="capitalize">
                {submittal?.description || 'N/A'}
              </Text>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="200px">
                Zone:
              </Text>
              <Text textTransform="capitalize">
                {submittal?.zone_level || 'N/A'}
              </Text>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="200px">
                Number:
              </Text>
              <Text textTransform="capitalize">
                {submittal?.number || 'N/A'}
              </Text>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="200px">
                Revision:
              </Text>
              <Text textTransform="capitalize">
                {submittal?.revision ?? 'N/A'}
              </Text>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="200px">
                Spec section:
              </Text>
              <Text textTransform="capitalize">
                {submittal?.spec_section || 'N/A'}
              </Text>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="200px">
                Package:
              </Text>
              <Text textTransform="capitalize">
                {submittal?.package || 'N/A'}
              </Text>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="200px">
                Submittal type:
              </Text>
              <Text textTransform="capitalize">
                {submittal?.submittal_type || 'N/A'}
              </Text>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="200px">
                Discipline:
              </Text>
              <Text textTransform="capitalize">
                {submittal?.discipline || 'N/A'}
              </Text>
            </Flex>

          </Flex>

          <Flex flex={1} flexDirection="column" gapY={2}>
            <Flex gapX={2}>
              <Text fontWeight="bold" width="200px">
                Ball-in-court:
              </Text>
              <Text textTransform="capitalize">
                {submittal?.ball_in_court || 'N/A'}
              </Text>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="200px">
                Due:
              </Text>
              {formattedDue ? (
                <Flex alignItems="center" textTransform="capitalize" as="span">
                  <Text>{formattedDue.date}</Text>
                  <LuDot />
                  <Text>{formattedDue.days}d</Text>
                </Flex>
              ) : (
                <Text>N/A</Text>
              )}
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="200px">
                Submitted:
              </Text>
              <Text>{formattedSubmitted || 'N/A'}</Text>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="200px">
                Returned:
              </Text>
              <Text>{formattedReturned || 'N/A'}</Text>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="200px">
                System:
              </Text>
              <Text textTransform="capitalize">
                {submittal?.system || 'N/A'}
              </Text>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="200px">
                Vendor:
              </Text>
              <Text textTransform="capitalize">
                {submittal?.vendor || 'N/A'}
              </Text>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="200px">
                Drawing Refs:
              </Text>
              <Text textTransform="capitalize">
                {submittal?.drawing_refs || 'N/A'}
              </Text>
            </Flex>

            <Flex gapX={2}>
              <Text fontWeight="bold" width="200px">
                Status:
              </Text>
              <Text textTransform="capitalize">
                {submittal?.status || 'N/A'}
              </Text>
            </Flex>

            <Flex gapX={2} alignItems="flex-start">
              <Text fontWeight="bold" width="200px">
                Meta Data:
              </Text>

              {submittal?.metadata && typeof submittal.metadata === 'object' ? (
                <Flex flexDirection="column" gapY={1}>
                  {Object.entries(submittal.metadata).map(([key, value]) => {
                    const label = key
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (char) => char.toUpperCase());

                    const formattedValue =
                      value === null || value === undefined || value === ''
                        ? 'N/A'
                        : Array.isArray(value)
                          ? value.join(', ')
                          : typeof value === 'object'
                            ? JSON.stringify(value)
                            : String(value);

                    return (
                      <Flex key={key} gapX={2} alignItems="flex-start">
                        <Text fontWeight="semibold" minWidth="140px">
                          {label}:
                        </Text>
                        <Text whiteSpace="pre-wrap" wordBreak="break-word">
                          {formattedValue}
                        </Text>
                      </Flex>
                    );
                  })}
                </Flex>
              ) : (
                <Text whiteSpace="pre-wrap" wordBreak="break-word">
                  {submittal?.metadata ? String(submittal.metadata) : 'N/A'}
                </Text>
              )}
            </Flex>
          </Flex>
        </Flex>

        <Separator />

        <Flex flexDirection="column" gapY={2}>
          <Flex justifyContent="space-between" alignItems="center">
            <Text fontWeight="bold" fontSize="lg">
              Attachments
            </Text>
            <Button onClick={handleSubmitAttachments}>Add Attachments</Button>
          </Flex>
          <Flex width="full">
            <Suspense fallback={<Center>Loading attachments...</Center>}>
              <ViewAttachmentsCard attachments={submittal?.attachments ?? []} />
            </Suspense>
          </Flex>
        </Flex>

        <Separator />

        <Flex flexDirection="column" gapY={2}>
          <Flex justifyContent="space-between" alignItems="center">
            <Text fontWeight="bold" fontSize="lg">
              Returned Documents
            </Text>
            <Button onClick={handleSubmitReturnAttachments}>
              Add Returned Documents
            </Button>
          </Flex>
          <Flex width="full">
            <Suspense fallback={<Center>Loading returned documents...</Center>}>
              <ViewReturnAttachmentsCard
                attachments={returnAttachments}
                onDelete={(returnId) =>
                  deleteSubmittalReturnAttachmentMutation({
                    params: {
                      return_id: returnId,
                      submittal_id: submittal_id as number,
                    },
                  }).unwrap()
                }
              />
            </Suspense>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
export default SubmittalDetails;

const fileUploadFormSchema = z.object({
  attachmentFiles: z
    .array(z.file())
    .refine(
      (files) => files.every((f) => f.size <= ATTACHMENT_MAX_FILE_SIZE_BYTES),
      { message: `Each file must be ${ATTACHMENT_MAX_FILE_SIZE_LABEL} or less.` },
    )
    .optional(),
});
type FileUploadFormType = z.infer<typeof fileUploadFormSchema>;

const toOptionalEnum = <T extends [string, ...string[]]>(values: T) =>
  z
    .union([z.enum(values), z.literal('')])
    .optional()
    .transform((v) => (v === '' ? undefined : v));

const submittalExportPdfFormSchema = z.object({
  to_name: z.string().optional(),
  to_address: z.string().optional(),
  attention: z.string().optional(),

  items_shipped_via: toOptionalEnum([
    'us_mail',
    'overnight',
    'courier',
    'karas_delivered',
    'ground',
    'uploaded_procore',
    'email',
    'other',
  ]),

  items_shipped_other: z.string().optional(),

  these_items_submitted: toOptionalEnum([
    'for_reference_only',
    'for_information_only',
    'for_review_comment',
    'out_for_approval',
    'for_final_approval',
    'for_record_distribution',
  ]),

  remarks: z.string().optional(),
  copy_to: z.string().optional(),
});

type SubmittalExportPdfFormType = z.infer<typeof submittalExportPdfFormSchema>;